// src/components/NavBar.jsx

import React, { memo, useContext, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import "../css/NavBar.css";

// ====================================================================
// 1. A memoized component for links that are ALWAYS visible.
// This component will render only ONCE. It has no props or state.
// ====================================================================
const StaticNavLinks = memo(() => {
  console.log("Rendering StaticNavLinks (should be once)");

  return (
    <>
      <li>
        <Link to="/">Home</Link>
      </li>

      <li className="dropdown">
        <span>Items</span>
        <ul className="dropdown-menu">
          <li>
            <Link to="/lost-items">Find Lost Items</Link>
          </li>
          <li>
            <Link to="/new-item">Upload an Item</Link>
          </li>
        </ul>
      </li>

      <li>
        <Link to="/contact">Contact Us</Link>
      </li>
    </>
  );
});

// ====================================================================
// 2. A memoized component for the dynamic auth-related links.
// This small component will re-render ONLY when auth.token changes.
// ====================================================================
const AuthNavLinks = memo(() => {
  const { auth } = useContext(AuthContext);
  console.log("Rendering AuthNavLinks (when auth changes)");

  return auth.token ? (
    <li className="dropdown">
      <span>Profile</span>
      <ul className="dropdown-menu">
        <li>
          <Link to="/profile">My Items</Link>
        </li>
        <li>
          <Link to="/notifications">Notifications</Link>
        </li>
        <li>
          <Link to="/messages">Messages</Link>
        </li>
      </ul>
    </li>
  ) : (
    <li>
      <Link to="/login-signup">Login / Sign Up</Link>
    </li>
  );
});

// ====================================================================
// 3. A memoized component for the user info and logout button.
// This also re-renders ONLY when auth state changes.
// It is separate to keep the component structure clean.
// ====================================================================
const UserInfoSection = memo(({ onLogout }) => {
  const { auth } = useContext(AuthContext);
  console.log("Rendering UserInfoSection (when auth changes)");

  if (!auth.token) {
    return null; // Render nothing if not logged in
  }

  return (
    <div className="user-info">
      <span className="username">{auth.user?.username}</span>
      <button onClick={onLogout} className="signout-button">
        Sign Out
      </button>
    </div>
  );
});

// ====================================================================
// 4. The main NavBar component that assembles the optimized pieces.
// ====================================================================
const NavBar = () => {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  // 'useCallback' memoizes the handleLogout function. It won't be
  // recreated on every render, making it a stable prop for UserInfoSection.
  const handleLogout = useCallback(() => {
    logout();
    navigate("/login-signup");
  }, [logout, navigate]); // Dependencies array

  return (
    <nav className="navbar">
      <div className="navbar-container container">
        <div className="logo">
          <Link to="/">Lost &amp; Found</Link>
        </div>

        <ul className="nav-links">
          {/* Render the optimized, memoized components */}
          <StaticNavLinks />
          <AuthNavLinks />
        </ul>

        <UserInfoSection onLogout={handleLogout} />
      </div>
    </nav>
  );
};

export default memo(NavBar);
